import {
  annotationsActions,
  applicationSelectors,
  authSelectors,
  readerActions,
  readerSelectors,
  useAppDispatch,
} from '@readcloud/state';
import React from 'react';
import { useSelector } from 'react-redux';
import { TextSelectionContext } from '../../context/TextSelection';
import { getType } from '../../constants';
import { LearningToolbar } from './LearningToolbar/LearningToolbar';

export const ConnectedLearningToolbar: React.FC<
  Pick<React.ComponentProps<typeof LearningToolbar>, 'container'> & {
    className?: string;
    positionFixed?: boolean;
    location?: string;
  }
> = (props) => {
  const readerData = useSelector(readerSelectors.getReaderData);
  const appData = useSelector(applicationSelectors.getApplcationState);
  const user = useSelector(authSelectors.getUser);
  const dispatch = useAppDispatch();

  const { selectionState } = React.useContext(TextSelectionContext);
  const tts = undefined; //React.useContext(TTSContainer.Context).tts;

  const [openConfirmation, setConfirmation] = React.useState<boolean>(false);

  const [boundingRect, setBoundingRect] = React.useState<DOMRect>();

  // epubjs uses a strange method to navigate to pages.
  // the iframe window itself is massive e.g. 12800px wide, instead of a container div inside being wide.
  // it then shifts the whole iframe window to the left/right when navigating.
  // so calling getBoundingClientRect (used by popperjs to find the correct position) returns some very large numbers because it is basing its values off the top/left of the window.
  // You could be looking at the last page where the window's position is `left: -8000px`, and getboundingClientRect will still return `left: 8xxx px`.
  // which then renders the popover that is not inside of this window, off screen, 8000px to the left of the outer window.

  // to counter this, we basically subtract the position of the iframe window from the result of getBoundingClientRect on the selection.
  // and then we pass that to popperjs by mocking a Range object.

  React.useEffect(() => {
    if (selectionState?.nativeRange && props.container) {
      const containerBounds = props.container.getBoundingClientRect();
      const selectionBounds =
        selectionState?.nativeRange?.getBoundingClientRect();
      let tempBounds = undefined;
      if (props.positionFixed) {
        tempBounds = new DOMRect(
          selectionBounds.x + containerBounds.x,
          selectionBounds.y + containerBounds.y,
          selectionBounds.width,
          selectionBounds.height
        );
      } else {
        tempBounds = new DOMRect(
          selectionBounds.x - containerBounds.x,
          selectionBounds.y - containerBounds.y,
          selectionBounds.width,
          selectionBounds.height
        );
      }
      setBoundingRect(tempBounds);
    } else {
      setBoundingRect(undefined);
    }
  }, [props.container, selectionState?.nativeRange, props.positionFixed]);

  return readerData.bookId && user && readerData.cloudId ? (
    <LearningToolbar
      {...props}
      selectedText={selectionState?.selectedText}
      selectionLocation={selectionState?.location || '1'}
      location={props.location}
      range={
        boundingRect && ({ getBoundingClientRect: () => boundingRect } as Range)
      }
      container={props.container}
      selecting={selectionState?.selecting}
      tts={tts}
      disableInfo={!appData.online}
      disableDictionary={!appData.online}
      disableCopy={
        readerData?.copyRights?.copyPercentage === 0 &&
        getType(readerData.fileUrl) === 'PDF'
      }
      disableTTS={!(user?.permissions?.indexOf('TTS') !== -1)}
      disableImmersive={!(user?.permissions?.indexOf('Immersive') !== -1)}
      currentlyFilteringText={readerData?.viewState?.annotationFilter}
      saveAnnotationFilter={(filter) =>
        dispatch(
          readerActions.asyncActions.updateViewState({
            annotationFilter: filter,
          })
        )
      }
      openConfirmation={openConfirmation}
      setConfirmation={setConfirmation}
      currentAuthor={user.id}
      filterUsers={readerData?.viewState?.annotationFilterUsers}
      setUserFilter={(filter) =>
        dispatch(
          readerActions.asyncActions.updateViewState({
            annotationFilterUsers: filter,
          })
        )
      }
      filterAnnotationBy={(filter, users) =>
        dispatch(
          annotationsActions.asyncActions.setAnnotationFilter({
            types: filter,
            users,
          })
        )
      }
      isOnline={appData.online}
      checkOnline={() => true} // appContainer.checkOnlineStatus <--- always returns true
    />
  ) : null;
};
